import { useTranslation } from 'react-i18next'
import { KibCheckbox } from '@chewy/kib-choices-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import {
  KibCloseIcon,
  KibIcon,
  KibWarningFillIcon,
} from '@chewy/kib-icons-react'
import { KibContainer, KibGrid, KibGridItem } from '@chewy/kib-layout-react'
import classNames from 'classnames'

import { ConversationTransport } from '~/api/graphql/generated/types'
import ConversationMessageFormattingArea from '~/components/dashboard/communications/ConversationMessageFormattingArea'
import SystemMessageAlert from '~/components/dashboard/communications/conversations-chat/SystemMessageAlert'
import { useMessageFormatting } from '~/store/hooks/conversations'

import type { MessageFormattingAreaProps } from './MessageFormattingArea'

import '@chewy/kib-patterns/dist/styles/kib-icon.css'
import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import styles from './KibMessageFormattingArea.module.scss'

const KibMessageFormattingArea = ({
  conversationId,
  disabled = false,
  hideStatusUpdateButtons = false,
}: MessageFormattingAreaProps) => {
  const { t } = useTranslation(['Common', 'Communications', 'Clients'])
  const {
    messageFormattingAreaExpanded,
    handleCloseMessageFormattingArea,
    canSendMessage,
    clientId,
    conversation,
    messageField,
    patientId,
    appointment,
    conversationFormattingAreaRef,
    transport,
    showSendButton,
    isLoading,
    handleMessageSend,
    showReplyButton,
    handleReplyClick,
    showReplyInNewThreadButton,
    handleReplyInNewThreadClick,
    handleMessageDraftSend,
    shouldShowCheckbox,
    isMedical,
    onInternalNoteAdd,
    isDeliveryFailed,
    onArchiveAction,
    archiveActionTitle,
    isSystemMessage,
  } = useMessageFormatting({ conversationId })

  if (isSystemMessage && transport === ConversationTransport.Email) {
    return (
      <KibContainer
        className={classNames(
          styles.messageInputContainer,
          styles.systemMessage,
        )}
      >
        <SystemMessageAlert {...{ clientId, patientId }} />
      </KibContainer>
    )
  }

  return (
    <KibContainer className={styles.messageInputContainer}>
      {messageFormattingAreaExpanded && (
        <KibGrid className={styles.headerClose}>
          <KibButtonNew
            iconOnly
            emphasis="tertiary"
            icon={<KibCloseIcon />}
            size="small"
            theme="utility"
            onClick={handleCloseMessageFormattingArea}
          >
            {t('Common:CLOSE_ACTION')}
          </KibButtonNew>
        </KibGrid>
      )}
      {canSendMessage && messageFormattingAreaExpanded && (
        <ConversationMessageFormattingArea
          hideEmail
          hideTo
          isChirp
          showAttachment
          clientId={clientId}
          eventId={conversation?.eventId}
          isShowSubject={false}
          maxEditorHeight={200}
          messageField={messageField}
          minEditorHeight={150}
          patientId={patientId || appointment?.patient}
          ref={conversationFormattingAreaRef}
          transport={transport}
        />
      )}
      <KibGrid
        className={classNames({
          [styles.messageCompose]: messageFormattingAreaExpanded,
        })}
        valign="center"
      >
        {!messageFormattingAreaExpanded ? (
          <>
            {showReplyButton && (
              <>
                <KibGridItem>
                  <KibButtonNew
                    disabled={isLoading || disabled}
                    onClick={handleReplyClick}
                  >
                    {t('Common:REPLY')}
                  </KibButtonNew>
                </KibGridItem>
                {showReplyInNewThreadButton && (
                  <KibGridItem>
                    <KibButtonNew
                      disabled={isLoading || disabled}
                      emphasis="secondary"
                      onClick={handleReplyInNewThreadClick}
                    >
                      {t('Communications:REPLY_IN_NEW_THREAD')}
                    </KibButtonNew>
                  </KibGridItem>
                )}
              </>
            )}
            <KibGridItem>
              <KibButtonNew
                disabled={isLoading}
                emphasis="secondary"
                onClick={onInternalNoteAdd}
              >
                {t('Common:ADD_INTERNAL_NOTE')}
              </KibButtonNew>
            </KibGridItem>

            {!hideStatusUpdateButtons && !isDeliveryFailed && (
              <KibGridItem>
                <KibButtonNew
                  disabled={isLoading}
                  emphasis="secondary"
                  onClick={onArchiveAction}
                >
                  {archiveActionTitle}
                </KibButtonNew>
              </KibGridItem>
            )}
          </>
        ) : (
          <>
            {canSendMessage && showSendButton && (
              <KibGridItem>
                <KibButtonNew disabled={isLoading} onClick={handleMessageSend}>
                  {t('Common:SEND_ACTION')}
                </KibButtonNew>
              </KibGridItem>
            )}
            {!canSendMessage && (
              <KibGrid className={styles.smsDisabled} valign="center">
                <KibGridItem>
                  <KibIcon circle size="xs">
                    <KibWarningFillIcon />
                  </KibIcon>
                </KibGridItem>
                <KibGridItem>
                  <span>{t('Clients:SMS_CONSENT_DISABLED')}</span>
                </KibGridItem>
              </KibGrid>
            )}
            <KibGridItem>
              <KibButtonNew
                disabled={isLoading}
                emphasis="secondary"
                onClick={handleMessageDraftSend}
              >
                {t('Common:SAVE_AS_DRAFT')}
              </KibButtonNew>
            </KibGridItem>
            {shouldShowCheckbox && (
              <KibGridItem>
                <KibCheckbox
                  className={styles.checkbox}
                  label={isMedical.label}
                  selectedValue={isMedical.value}
                  onChange={() => isMedical.setValue(!isMedical.value)}
                />
              </KibGridItem>
            )}
          </>
        )}
      </KibGrid>
    </KibContainer>
  )
}

export default KibMessageFormattingArea
